import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Flame, Upload, X, Play, Pause, StopCircle, Plus, Search, Trash2, RefreshCw,
  ChevronDown, ChevronUp, AlertCircle, CheckCircle2, Clock, Users, Settings2,
  FileSpreadsheet, Image, FileText, ArrowUp, ArrowDown, Type, Music, Video,
  Building2, Send,
} from 'lucide-react';
import api from '../../services/api';

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}
function formatPhone(phone) {
  const d = String(phone).replace(/\D/g, '');
  if (d.length === 11) return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
  return phone;
}

const STATUS_CONFIG = {
  pendente: { label: 'Pendente', color: 'bg-gray-100 text-gray-600' },
  em_andamento: { label: 'Em andamento', color: 'bg-blue-100 text-blue-700' },
  pausada: { label: 'Pausada', color: 'bg-yellow-100 text-yellow-700' },
  concluida: { label: 'Concluída', color: 'bg-green-100 text-green-700' },
  cancelada: { label: 'Cancelada', color: 'bg-red-100 text-red-700' },
  erro: { label: 'Erro', color: 'bg-red-100 text-red-700' },
};
function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pendente;
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cfg.color}`}>{cfg.label}</span>;
}

const STEP_LABELS = { texto: 'Texto', imagem: 'Imagem', video: 'Vídeo', audio: 'Áudio', documento: 'Documento' };
const STEP_ICONS = { texto: Type, imagem: Image, video: Video, audio: Music, documento: FileText };

const SHORTCODES = [
  { code: '#primeironome', desc: 'Primeiro nome' },
  { code: '#nomecompleto', desc: 'Nome completo' },
  { code: '#periodododia', desc: 'Bom dia/tarde/noite' },
  { code: '#diasemana', desc: 'Dia da semana' },
];

function StepCard({ step, index, total, onUpdate, onRemove, onMoveUp, onMoveDown }) {
  const fileRef = useRef(null);
  const textRef = useRef(null);
  const Icon = STEP_ICONS[step.tipo];
  const handleFile = (e) => { const file = e.target.files[0]; if (!file) return; onUpdate({ ...step, arquivo: file, preview: URL.createObjectURL(file), nomeOriginal: file.name }); e.target.value = ''; };
  const insertShortcode = (code) => {
    const ta = textRef.current;
    if (!ta) { onUpdate({ ...step, conteudo: (step.conteudo || '') + code }); return; }
    const start = ta.selectionStart;
    const text = step.conteudo || '';
    const newText = text.substring(0, start) + code + text.substring(ta.selectionEnd);
    onUpdate({ ...step, conteudo: newText });
  };
  return (
    <div className="border border-gray-200 rounded-xl p-3 bg-white space-y-2">
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5 bg-gray-100 px-2 py-1 rounded-lg">
          <Icon size={13} className="text-gray-600" />
          <span className="text-xs font-semibold text-gray-600">{index + 1}. {STEP_LABELS[step.tipo]}</span>
        </div>
        <div className="flex-1" />
        <button onClick={onMoveUp} disabled={index === 0} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><ArrowUp size={14} /></button>
        <button onClick={onMoveDown} disabled={index === total - 1} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"><ArrowDown size={14} /></button>
        <button onClick={onRemove} className="p-1 text-gray-400 hover:text-red-500"><Trash2 size={14} /></button>
      </div>
      {step.tipo === 'texto' && (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            {SHORTCODES.map(s => (
              <button key={s.code} onClick={() => insertShortcode(s.code)} className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded hover:bg-blue-100">{s.code}</button>
            ))}
          </div>
          <textarea ref={textRef} value={step.conteudo || ''} onChange={e => onUpdate({ ...step, conteudo: e.target.value })} placeholder="Digite a mensagem..." rows={3} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-y" />
        </div>
      )}
      {['imagem', 'video', 'audio', 'documento'].includes(step.tipo) && (
        <div>
          {step.preview ? (
            <div className="flex items-center gap-3">
              {step.tipo === 'imagem' && <img src={step.preview} alt="" className="h-12 w-12 object-cover rounded border" />}
              {step.tipo !== 'imagem' && <FileText size={18} className="text-gray-400" />}
              <span className="text-xs text-gray-500 truncate flex-1">{step.nomeOriginal}</span>
              <button onClick={() => onUpdate({ ...step, arquivo: null, preview: null, nomeOriginal: '' })} className="text-gray-400 hover:text-red-500"><X size={14} /></button>
            </div>
          ) : (
            <button onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-gray-200 rounded-lg py-3 text-center text-sm text-gray-400 hover:border-blue-300 hover:text-blue-500">
              <Upload size={16} className="mx-auto mb-1 opacity-50" />Selecionar arquivo
            </button>
          )}
          <input ref={fileRef} type="file" className="hidden" onChange={handleFile} />
        </div>
      )}
    </div>
  );
}

export default function Aquecimento() {
  const qc = useQueryClient();
  const [nome, setNome] = useState('');

  const [modelosMeta, setModelosMeta] = useState([]);
  const [modeloSelecionado, setModeloMetaSelecionado] = useState('');
  const [modeloHeaderTipo, setModeloHeaderTipo] = useState(null); // 'IMAGE' | 'VIDEO' | null
  const [metaHeaderFile, setMetaHeaderFile] = useState(null);
  const [loadingModelos, setLoadingModelos] = useState(false);
  const [erroModelos, setErroModelos] = useState('');
  const [provedor, setProvedor] = useState('evolution');
  const [instancia, setInstancia] = useState('');
  const metaHeaderRef = useRef(null);

  useEffect(() => {
    if (provedor === 'meta_oficial' && instancia) {
      setLoadingModelos(true);
      setErroModelos('');
      setModelosMeta([]);
      setModeloMetaSelecionado('');
      api.get(`/whatsapp-oficial/${instancia}/modelos`)
        .then(r => { setModelosMeta(r.data.data || []); })
        .catch(err => { setErroModelos(err.response?.data?.error || 'Erro ao buscar modelos da Meta'); })
        .finally(() => setLoadingModelos(false));
    } else {
      setModelosMeta([]);
      setModeloMetaSelecionado('');
      setErroModelos('');
    }
  }, [provedor, instancia]);
  const [empreendimento, setEmpreendimento] = useState('');
  const [contextoIa, setContextoIa] = useState('');
  const [contatos, setContatos] = useState([]);
  const [steps, setSteps] = useState([]);
  const [materiais] = useState([]);
  const [delay, setDelay] = useState(4);
  const [pauseEvery, setPauseEvery] = useState(20);
  const [pauseDuration, setPauseDuration] = useState(60);
  const [limiteDiario, setLimiteDiario] = useState(0);
  const [creating, setCreating] = useState(false);
  const [ignorados, setIgnorados] = useState(0);
  const [view, setView] = useState('list');
  const excelRef = useRef(null);

  const { data: instancias } = useQuery({ queryKey: ['aq-instancias'], queryFn: () => api.get('/aquecimento/instancias').then(r => r.data.data) });
  const { data: instanciasMeta } = useQuery({ queryKey: ['aq-instancias-meta'], queryFn: () => api.get('/aquecimento/instancias-meta').then(r => r.data.data) });
  const { data: empreendimentos } = useQuery({ queryKey: ['aq-empreendimentos'], queryFn: () => api.get('/aquecimento/empreendimentos').then(r => r.data.data) });
  const { data: campanhas, refetch } = useQuery({ queryKey: ['aq-campanhas'], queryFn: () => api.get('/aquecimento').then(r => r.data.data), refetchInterval: 5000 });

  const handleExcel = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append('excel', file);
    try {
      const { data } = await api.post('/aquecimento/parse-excel', fd);
      setContatos(data.data);
      setIgnorados(data.ignorados_blacklist || 0);
    } catch (err) { alert('Erro ao ler planilha'); }
    e.target.value = '';
  };

  const addStep = (tipo) => setSteps(prev => [...prev, { tipo, conteudo: '', arquivo: null, preview: null, nomeOriginal: '' }]);
  const updateStep = (i, s) => setSteps(prev => prev.map((x, j) => j === i ? s : x));
  const removeStep = (i) => setSteps(prev => prev.filter((_, j) => j !== i));
  const moveStep = (i, dir) => {
    const arr = [...steps]; const t = arr[i]; arr[i] = arr[i + dir]; arr[i + dir] = t; setSteps(arr);
  };

  const handleCreate = async () => {
    if (!nome || !instancia || !empreendimento || !contatos.length) {
      alert('Preencha todos os campos obrigatórios'); return;
    }
    if (provedor === 'meta_oficial' && !modeloSelecionado) {
      alert('Selecione um modelo de mensagem da Meta'); return;
    }
    if (provedor === 'meta_oficial' && modeloHeaderTipo && !metaHeaderFile) {
      alert(`Este template requer ${modeloHeaderTipo === 'IMAGE' ? 'uma imagem' : 'um vídeo'} no cabeçalho. Faça o upload antes de continuar.`); return;
    }
    if (provedor !== 'meta_oficial' && !steps.length) {
      alert('Adicione pelo menos um bloco na esteira de mensagens'); return;
    }
    setCreating(true);
    try {
      const fd = new FormData();
      const config = { nome, instancia_evolution: instancia, instancia_tipo: provedor, empreendimento_id: empreendimento, contexto_ia: contextoIa, delay_segundos: delay, pausa_a_cada: pauseEvery, duracao_pausa_segundos: pauseDuration, limite_diario: limiteDiario, contatos, steps: steps.map(s => ({ tipo: s.tipo, conteudo: s.conteudo || '', legenda: s.legenda || '', nomeOriginal: s.nomeOriginal || '' })), modelo_meta: modeloSelecionado, autostart: true };
      fd.append('config', JSON.stringify(config));
      steps.forEach((s, i) => { if (s.arquivo) fd.append(`step_${i}`, s.arquivo); });
      materiais.forEach((m, i) => fd.append(`material_${i}`, m.file));
      if (metaHeaderFile) fd.append('meta_header_file', metaHeaderFile);
      await api.post('/aquecimento', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setView('list'); refetch();
      setNome(''); setContatos([]); setSteps([]); setContextoIa('');
      setModeloMetaSelecionado(''); setModeloHeaderTipo(null); setMetaHeaderFile(null);
      setProvedor('evolution'); setInstancia(''); setModelosMeta([]);
    } catch (err) { alert(err.response?.data?.error || 'Erro ao criar campanha'); }
    setCreating(false);
  };

  const handleAction = async (id, action) => {
    try { await api.post(`/aquecimento/${id}/${action}`); refetch(); } catch {}
  };
  const handleCancel = async (id) => {
    if (!window.confirm('Cancelar campanha?')) return;
    try { await api.delete(`/aquecimento/${id}`); refetch(); } catch {}
  };

  const handleDelete = async (id, nome) => {
    if (!window.confirm(`Excluir permanentemente a campanha "${nome}"?\n\nEsta ação não pode ser desfeita.`)) return;
    try { await api.delete(`/aquecimento/${id}/permanente`); refetch(); } catch (err) { alert(err.response?.data?.error || 'Erro ao excluir'); }
  };

  if (view === 'create') return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Flame className="text-orange-500" size={24} />
          <h1 className="text-2xl font-bold text-gray-800">Nova Campanha de Aquecimento</h1>
        </div>
        <button onClick={() => { setView('list'); setProvedor('evolution'); setInstancia(''); setModelosMeta([]); setModeloMetaSelecionado(''); setModeloHeaderTipo(null); setMetaHeaderFile(null); }} className="text-sm text-gray-500 hover:text-gray-700">← Voltar</button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">Nome da Campanha *</label>
          <input value={nome} onChange={e => setNome(e.target.value)} placeholder="Ex: Aquecimento Ingarden Maio" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-600 block">Provedor de Envio *</label>
          <div className="flex gap-2">
            <button type="button" onClick={() => { setProvedor('evolution'); setInstancia(''); }} className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold border transition-colors ${provedor === 'evolution' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
              Evolution API
            </button>
            <button type="button" onClick={() => { setProvedor('meta_oficial'); setInstancia(''); }} className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold border transition-colors ${provedor === 'meta_oficial' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
              API Oficial Meta
            </button>
          </div>
          {provedor === 'meta_oficial' ? (
            (instanciasMeta || []).length === 0
              ? <div className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">Nenhum número da API Oficial configurado. Vá em "API Oficial Meta" no menu.</div>
              : <select value={instancia} onChange={e => setInstancia(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
                  <option value="">Selecione o número...</option>
                  {(instanciasMeta || []).map(n => <option key={n.id} value={n.id}>{n.nome}{n.phone_display ? ` — ${n.phone_display}` : ''}</option>)}
                </select>
          ) : (
            <select value={instancia} onChange={e => setInstancia(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
              <option value="">Selecione a instância...</option>
              {(instancias || []).map(i => <option key={i.name || i.instanceName} value={i.name || i.instanceName}>{i.name || i.instanceName}</option>)}
            </select>
          )}
        </div>
      </div>

      {provedor === 'meta_oficial' && instancia && (
        <div className="border border-green-200 bg-green-50 rounded-xl p-4 space-y-2">
          <label className="text-xs font-semibold text-green-800 block">Modelo de Mensagem (Meta) *</label>
          {loadingModelos && (
            <div className="flex items-center gap-2 text-sm text-green-700">
              <RefreshCw size={14} className="animate-spin" /> Carregando modelos aprovados da Meta...
            </div>
          )}
          {erroModelos && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <AlertCircle size={14} /> {erroModelos}
            </div>
          )}
          {!loadingModelos && !erroModelos && modelosMeta.length === 0 && (
            <div className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">
              Nenhum modelo aprovado encontrado nesta conta Meta.
            </div>
          )}
          {!loadingModelos && modelosMeta.length > 0 && (
            <>
              <select value={modeloSelecionado} onChange={e => {
                const name = e.target.value;
                setModeloMetaSelecionado(name);
                setMetaHeaderFile(null);
                if (name) {
                  const tmpl = modelosMeta.find(m => m.name === name);
                  setModeloHeaderTipo(tmpl?.header_type || null);
                } else {
                  setModeloHeaderTipo(null);
                }
              }} className="w-full border border-green-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-400">
                <option value="">Selecione um modelo aprovado...</option>
                {modelosMeta.map(m => (
                  <option key={m.id} value={m.name}>{m.name} — {m.language}{m.header_type ? ` 📎 ${m.header_type}` : ''}</option>
                ))}
              </select>
              {modeloHeaderTipo && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 space-y-2">
                  <p className="text-xs font-semibold text-blue-800">Este template requer {modeloHeaderTipo === 'IMAGE' ? 'uma imagem' : 'um vídeo'} no cabeçalho *</p>
                  {metaHeaderFile ? (
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={14} className="text-green-600" />
                      <span className="text-xs text-gray-700 truncate flex-1">{metaHeaderFile.name}</span>
                      <button onClick={() => setMetaHeaderFile(null)} className="text-gray-400 hover:text-red-500"><X size={13} /></button>
                    </div>
                  ) : (
                    <button onClick={() => metaHeaderRef.current?.click()} className="flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900 underline">
                      <Upload size={12} /> Selecionar {modeloHeaderTipo === 'IMAGE' ? 'imagem' : 'vídeo'}
                    </button>
                  )}
                  <input ref={metaHeaderRef} type="file" className="hidden"
                    accept={modeloHeaderTipo === 'IMAGE' ? 'image/*' : 'video/*'}
                    onChange={e => { setMetaHeaderFile(e.target.files[0] || null); e.target.value = ''; }} />
                </div>
              )}
              <p className="text-[10px] text-green-700">Ao usar modelo Meta, o template será disparado como mensagem inicial. O Iago continuará a conversa normalmente após a resposta.</p>
            </>
          )}
        </div>
      )}

      <div>
        <label className="text-xs font-medium text-gray-600 mb-1 block">Empreendimento *</label>
        <select value={empreendimento} onChange={e => setEmpreendimento(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
          <option value="">Selecione o empreendimento...</option>
          {(empreendimentos || []).map(emp => <option key={emp.id} value={emp.id}>{emp.nome} ({emp.status})</option>)}
        </select>
      </div>

      <div>
        <label className="text-xs font-medium text-gray-600 mb-1 block">Contexto extra para o Iago (opcional)</label>
        <textarea value={contextoIa} onChange={e => setContextoIa(e.target.value)} placeholder="Informações adicionais sobre o empreendimento, condições especiais, etc." rows={3} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-semibold text-gray-700">Esteira de Mensagens (disparo inicial)</label>
          <div className="flex gap-1">
            {Object.entries(STEP_LABELS).map(([tipo, label]) => {
              const Icon = STEP_ICONS[tipo];
              return <button key={tipo} onClick={() => addStep(tipo)} className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200"><Icon size={12} />{label}</button>;
            })}
          </div>
        </div>
        {steps.map((step, i) => (
          <StepCard key={i} step={step} index={i} total={steps.length} onUpdate={s => updateStep(i, s)} onRemove={() => removeStep(i)} onMoveUp={() => moveStep(i, -1)} onMoveDown={() => moveStep(i, 1)} />
        ))}
        {steps.length === 0 && <p className="text-sm text-gray-400 text-center py-4 border-2 border-dashed rounded-xl">Adicione blocos à esteira usando os botões acima</p>}
      </div>

      <div className="space-y-3">
        <label className="text-sm font-semibold text-gray-700">Contatos</label>
        <div className="flex items-center gap-3">
          <button onClick={() => excelRef.current?.click()} className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg text-sm font-medium hover:bg-green-100">
            <FileSpreadsheet size={16} /> Importar Excel
          </button>
          <input ref={excelRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleExcel} />
          {contatos.length > 0 && <span className="text-sm text-gray-600">{contatos.length} contatos carregados</span>}
          {ignorados > 0 && <span className="text-sm text-red-500">{ignorados} ignorados (blacklist)</span>}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <div>
          <label className="text-xs text-gray-500">Delay entre envios (s)</label>
          <input type="number" min={1} value={delay} onChange={e => setDelay(Number(e.target.value))} className="w-full border rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-xs text-gray-500">Pausar a cada N envios</label>
          <input type="number" min={0} value={pauseEvery} onChange={e => setPauseEvery(Number(e.target.value))} className="w-full border rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-xs text-gray-500">Duração da pausa (s)</label>
          <input type="number" min={0} value={pauseDuration} onChange={e => setPauseDuration(Number(e.target.value))} className="w-full border rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-xs text-gray-500">Limite diário de disparos</label>
          <input type="number" min={0} value={limiteDiario} onChange={e => setLimiteDiario(Number(e.target.value))} className="w-full border rounded-lg px-3 py-2 text-sm" />
          <p className="text-[10px] text-gray-400 mt-1">0 = sem limite. Ao atingir, pausa até meia-noite.</p>
        </div>
      </div>

      <button onClick={handleCreate} disabled={creating} className="w-full py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2">
        <Send size={18} /> {creating ? 'Criando...' : 'Criar e Iniciar Campanha'}
      </button>
    </div>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Flame className="text-orange-500" size={24} />
          <h1 className="text-2xl font-bold text-gray-800">Aquecimento de Leads</h1>
        </div>
        <button onClick={() => setView('create')} className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600">
          <Plus size={16} /> Nova Campanha
        </button>
      </div>

      <div className="space-y-3">
        {(!campanhas || campanhas.length === 0) ? (
          <div className="text-center py-12 text-gray-400">
            <Flame size={40} className="mx-auto mb-3 opacity-30" />
            <p>Nenhuma campanha de aquecimento criada</p>
          </div>
        ) : campanhas.map(c => (
          <div key={c.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-gray-800">{c.nome}</span>
                <StatusBadge status={c.status} />
              </div>
              <div className="flex items-center gap-4 text-xs text-gray-500">
                {c.empreendimento_nome && <span className="flex items-center gap-1"><Building2 size={12} />{c.empreendimento_nome}</span>}
                <span className="flex items-center gap-1"><Users size={12} />{c.enviados}/{c.total} enviados</span>
                {c.limite_diario > 0 && <span className="flex items-center gap-1 text-orange-500"><Settings2 size={12} />{c.limite_diario}/dia</span>}
                {c.erros > 0 && <span className="text-red-500">{c.erros} erros</span>}
                {c.proxima_retomada && <span className="flex items-center gap-1 text-yellow-600"><Clock size={12} />Retoma {fmtDate(c.proxima_retomada)}</span>}
                <span className="flex items-center gap-1"><Clock size={12} />{fmtDate(c.criado_em)}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {c.status === 'pendente' && <button onClick={() => handleAction(c.id, 'iniciar')} className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100"><Play size={16} /></button>}
              {c.status === 'em_andamento' && <button onClick={() => handleAction(c.id, 'pausar')} className="p-2 bg-yellow-50 text-yellow-600 rounded-lg hover:bg-yellow-100"><Pause size={16} /></button>}
              {c.status === 'pausada' && <button onClick={() => handleAction(c.id, 'retomar')} className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"><Play size={16} /></button>}
              {['pendente', 'em_andamento', 'pausada'].includes(c.status) && <button onClick={() => handleCancel(c.id)} className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100"><StopCircle size={16} /></button>}
              {['concluida', 'cancelada', 'erro'].includes(c.status) && (
                <button onClick={() => handleDelete(c.id, c.nome)} title="Excluir campanha" className="p-2 bg-gray-50 text-gray-400 rounded-lg hover:bg-red-50 hover:text-red-500">
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
